import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { BdsTableCell, BdsTableRow, BdsTypo } from 'blip-ds/dist/blip-ds-react';
import DailyActiveUsersContext from '../../../../contexts/DailyActiveUsersContext';
import { getMonthTwoDigits } from '../../../../utils/date';
import { DailyActiveUserPaper } from '../styles';
import { PageHeaderComponent } from '../../components/Metrics/PageHeader/PageHeaderComponent';
import { trackEvent } from '../../../../api/Billing/Tracking';
import { origin, REPORT_PERIOD_SELECT } from '../../../../shared/SegmentEvents';
import FeatureToggleService from '../../../../services/FeatureToggleService';
import { BillingMetrics } from '../../../../shared/BillingMetrics';
import TableWithPagination from '../../../../components/TableWithPagination/TableWithPagination';
import { useSortedPaginatedData } from '../../../../hooks/useSortedPaginatedData';
import { DEFAULT_PAGE_OPTIONS, COLUMN_WIDTHS_2 as COLUMN_WIDTHS } from '../../../../utils/paginationUtils';

const COLUMN_KEYS = ['botName', 'total'];

export interface DailyActiveUserMonthlyUsageProps {
  isDetailedView?: boolean;
}

const DailyActiveUserMonthlyUsageComponent: FC<DailyActiveUserMonthlyUsageProps> = ({
  isDetailedView = false,
}) => {
  const {
    dateToSearch,
    content,
    tenant,
    loggedUser,
    setDailyActiveUserMonthlyUsage,
    dailyActiveUserMonthlyUsage,
    billingDailyActiveUserApi,
    periodOptions,
    setDateToSearch,
    trackingProps,
    getPlanStatus,
  } = useContext(DailyActiveUsersContext);
  const [isLoading, setLoading] = useState(false);

  const { itemsPerPage, sortConfig, sortedData, paginatedData, handlePaginationChange, handleItemsPerPageChange, handleSortChange } =
    useSortedPaginatedData({ data: dailyActiveUserMonthlyUsage });

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true);
        const storageType =
          (await FeatureToggleService.isDatabricksStorageTypeEnabled(BillingMetrics.dailyActiveUser)) ? 1 : 0;
        const monthString = `${dateToSearch.getFullYear()}-${getMonthTwoDigits(dateToSearch)}`;
        const data = await billingDailyActiveUserApi.getDailyActiveUserMonthlyUsage(
          tenant.info.id,
          monthString,
          -1,
          storageType,
        );
        setDailyActiveUserMonthlyUsage(data);
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, [dateToSearch]);

  const tableRows = useMemo(
    () =>
      paginatedData.length > 0
        ? paginatedData.map((item) => (
            <BdsTableRow key={`${item.botName}-${item.botId}`}>
              <BdsTableCell>
                <BdsTypo variant="fs-14" bold="bold" margin={false}>
                  {item.botName}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>
                  {item.total.toLocaleString(content.locale)}
                </BdsTypo>
              </BdsTableCell>
            </BdsTableRow>
          ))
        : undefined,
    [paginatedData, content.locale],
  );

  const periodOptionChangeEvent = async (selectedOption) => {
    const dateToRetrieveData = new Date(selectedOption.detail.value);
    setDateToSearch(dateToRetrieveData);
    const planStatus = await getPlanStatus();
    trackEvent(REPORT_PERIOD_SELECT, tenant, loggedUser, {
      ...trackingProps,
      origin: origin.DAUPERCHATBOT,
      date: dateToRetrieveData,
      planStatus,
    });
  };

  return (
    <>
      {isDetailedView && (
        <PageHeaderComponent
          title={content.chatbotDailyActiveUserConsuption.title}
          dateToSearch={dateToSearch}
          periodOptions={periodOptions}
          periodOptionChangeEvent={periodOptionChangeEvent}
        />
      )}
      <DailyActiveUserPaper>
        {!isDetailedView && (
          <BdsTypo variant="fs-24" bold="bold" margin={false}>
            {content.chatbotDailyActiveUserConsuption.title}
          </BdsTypo>
        )}
        <BdsTypo variant="fs-14" margin={false}>
          {content.chatbotDailyActiveUserConsuption.about}
        </BdsTypo>
        <TableWithPagination
          options={{
            heading: [
              content.chatbotDailyActiveUserConsuption.columnChatbot,
              content.chatbotDailyActiveUserConsuption.columnDailyActiveUsers,
            ],
            columnKeys: COLUMN_KEYS,
            columnWidths: COLUMN_WIDTHS,
            sortConfig,
            onSortChange: handleSortChange,
            itemsPerPageList: DEFAULT_PAGE_OPTIONS,
            itemsPerPage,
            numberOfItems: isLoading ? itemsPerPage : sortedData.length,
            isLoading,
            hasEmptyState: !isLoading && sortedData.length === 0,
            emptyStateSubtitle: '',
            isAutoHeight: true,
          }}
          onPaginationChange={handlePaginationChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        >
          {tableRows}
        </TableWithPagination>
      </DailyActiveUserPaper>
    </>
  );
};

export default DailyActiveUserMonthlyUsageComponent;
